import React, { useEffect, useState } from "react";
import Field from "@/components/shared/ui/Field";
import Input from "@/components/shared/ui/Input";
import { FiMapPin, FiHash, FiFlag } from "react-icons/fi";

export type EditAirportPayload = {
  airportName: string;
  airportCode: string;
  cityName?: string;
  cityCode?: string;
  countryName?: string;
  countryCode?: string;
};

export type EditAirportProps = {
  id: number | string;
  airportName: string;
  airportCode: string;
  cityName?: string;
  cityCode?: string;
  countryName?: string;
  countryCode?: string;
};

export default function UpdateAirport({
  airport,
  onSubmit,
  onCancel,
}: {
  airport: EditAirportProps;
  onSubmit: (payload: EditAirportPayload) => void;
  onCancel?: () => void;
}) {
  const [form, setForm] = useState<EditAirportPayload>({
    airportName: airport.airportName,
    airportCode: airport.airportCode,
    cityName: airport.cityName ?? "",
    cityCode: airport.cityCode ?? "",
    countryName: airport.countryName ?? "",
    countryCode: airport.countryCode ?? "",
  });

  useEffect(() => {
    setForm({
      airportName: airport.airportName,
      airportCode: airport.airportCode,
      cityName: airport.cityName ?? "",
      cityCode: airport.cityCode ?? "",
      countryName: airport.countryName ?? "",
      countryCode: airport.countryCode ?? "",
    });
  }, [airport]);

  const change = <K extends keyof EditAirportPayload>(k: K, v: EditAirportPayload[K]) =>
    setForm((p) => ({ ...p, [k]: v }));

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const airportName = (form.airportName ?? "").trim();
    const airportCode = (form.airportCode ?? "").trim().toUpperCase();
    if (!airportName || !airportCode) return;

    onSubmit({
      airportName,
      airportCode,
      cityName: form.cityName?.trim() || "",
      cityCode: form.cityCode?.trim().toUpperCase() || "",
      countryName: form.countryName?.trim() || "",
      countryCode: form.countryCode?.trim().toUpperCase() || "",
    });
  };

  return (
    <form onSubmit={submit} className="grid grid-cols-1 gap-4">
      <Field label="Airport name" icon={<FiMapPin />}>
        <Input value={form.airportName} onChange={(e) => change("airportName", e.target.value)} required />
      </Field>
      <Field label="Airport code" icon={<FiHash />}>
        <Input value={form.airportCode} onChange={(e) => change("airportCode", e.target.value)} required />
      </Field>
      <Field label="City name" icon={<FiMapPin />}>
        <Input value={form.cityName ?? ""} onChange={(e) => change("cityName", e.target.value)} />
      </Field>
      <Field label="City code" icon={<FiHash />}>
        <Input value={form.cityCode ?? ""} onChange={(e) => change("cityCode", e.target.value)} />
      </Field>
      <Field label="Country name" icon={<FiFlag />}>
        <Input value={form.countryName ?? ""} onChange={(e) => change("countryName", e.target.value)} />
      </Field>
      <Field label="Country code" icon={<FiHash />}>
        <Input value={form.countryCode ?? ""} onChange={(e) => change("countryCode", e.target.value)} />
      </Field>

      <div className="mt-3 flex justify-end gap-2">
        <button type="button" className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50" onClick={() => onCancel?.()}>Cancel</button>
        <button type="submit" className="rounded-lg bg-sky-600 px-3 py-2 text-sm font-semibold text-white hover:bg-sky-700">Save Changes</button>
      </div>
    </form>
  );
}
